import * as React from 'react';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';

import { AviaPassengerFareFragment, FareFamilyFragment, ResultsLegFragment } from '@websky/graphql';
import { useTheme } from '../../../theme';
import FlightInfoSegment from '../FlightInfoSegment/FlightInfoSegment';
import { getNextSegment } from '../../utils';
import { getDurationString, getFlightDuration } from '../../../Results/utils';
import SelectedFareGroup from '../SelectedFareGroup/SelectedFareGroup';
import { useGetFfpBonusInfoById } from '@features/fares';
import { useMemo } from 'react';

interface Props {
	className?: string;
	legId?: number;
	flightId?: string;
	flight: ResultsLegFragment;
	lastSelectedFare?: FareFamilyFragment;
	passengerFares?: AviaPassengerFareFragment[];
	onClose?: () => void;
	orderId?: string;
	hideConditionsButton?: boolean;
	hidePrice?: boolean;
	isReturnFlight?: boolean;
	isSelectorInModal?: boolean;
	isFareRulesDisabled?: boolean;
	isFaresMilesMustBeRequested?: boolean;
}

const DesktopFlightModalContentSelected: React.FC<Props> = ({
	className,
	legId,
	flight,
	lastSelectedFare,
	passengerFares,
	flightId,
	orderId,
	hideConditionsButton,
	isReturnFlight,
	isSelectorInModal = true,
	hidePrice,
	isFareRulesDisabled,
	isFaresMilesMustBeRequested
}) => {
	const { DesktopFlightModalContentSelected: css } = useTheme('DesktopFlight');
	const { t } = useTranslation('FlightInfoModalContent');

	const price = flight.pricesForFareGroups?.[0]?.prices?.[0].price || null;

	const flightIds = useMemo(() => [flightId], [flightId]);

	const segmentsForFlight = useMemo(() => flight?.segments?.map(segment => segment?.segment), [flight]);

	const { ffpBonusInfoById } = useGetFfpBonusInfoById({
		flightIds,
		legId,
		segments: segmentsForFlight,
		needToMakeRequest: isFaresMilesMustBeRequested
	});

	return (
		<div
			className={cn(css.contentWrapper, className, { [css.container_dropdown]: !isSelectorInModal })}
			data-test-id="selected-flight-modal"
		>
			<div className={cn(css.content, { [css.content__center]: !lastSelectedFare })}>
				{lastSelectedFare && (
					<SelectedFareGroup
						className={cn(css.flight, { [css.flight_thin]: flight.segments.length > 1 })}
						flightId={flightId}
						orderId={orderId}
						fare={lastSelectedFare}
						passengerFares={passengerFares}
						price={hidePrice ? null : price}
						hideConditionsButton={hideConditionsButton}
						isFareRulesDisabled={isFareRulesDisabled}
						ffpBonusInfo={ffpBonusInfoById[flightId]}
					/>
				)}

				<div
					className={cn(css.segments, {
						[css.segments__big]: flight.segments.length > 1
					})}
				>
					{flight.segments.map((segment, index) => (
						<div className={css.segment__flight} key={index}>
							<FlightInfoSegment
								className={css.segment__flightWrapper}
								cardClassName={css.segment__card}
								transferClassName={css.segment__transfer}
								segment={segment}
								nextSegment={segment.transferDuration ? getNextSegment(flight.segments, index) : null}
								isMobile={true}
								isReturnFlight={isReturnFlight}
							/>

							{flight.segments.length > 1 && flight.segments.length - 1 === index && (
								<div className={css.time}>
									{t('Total duration')}: {getDurationString(getFlightDuration(flight.segments))}
								</div>
							)}
						</div>
					))}
				</div>
			</div>
		</div>
	);
};

export default DesktopFlightModalContentSelected;
